import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { isAuthed, getUser } from "@/lib/api";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CalendarClock, Star, Home, BedDouble, ArrowRightLeft, Coffee, CalendarDays,
  BarChart3, Wallet, Store, Users, ShieldCheck, Sparkles, Briefcase, Plug,
  Receipt, ConciergeBell, Compass, HelpCircle, MessageCircle,
} from "lucide-react";
import PricingPreview from "@/components/PricingPreview";
import ReceptionTab from "./dashboard/ReceptionTab";
import SubscriptionCard from "./dashboard/SubscriptionCard";
import BoostCard from "./dashboard/BoostCard";
import CalendarTab from "./dashboard/CalendarTab";
import SuperuserTierSwitcher from "./dashboard/SuperuserTierSwitcher";
import StatCard from "./dashboard/StatCard";
import TabBadge from "./dashboard/TabBadge";
import AnalyticsTab from "./dashboard/AnalyticsTab";
import PropertyTab from "./dashboard/property/PropertyTab";
import AssistantIACard from "./dashboard/property/AssistantIACard";
import RoomsTab from "./dashboard/rooms/RoomsTab";
import BookingsTab from "./dashboard/bookings/BookingsTab";

// Lazy-loaded tab modules — split into separate chunks so the initial dashboard bundle stays small.
const ChannelsTab = lazy(() => import("./dashboard/ChannelsTab"));
const AdvancedAnalyticsTab = lazy(() => import("./dashboard/AdvancedAnalyticsTab"));
const HuntsTab = lazy(() => import("./dashboard/HuntsTab"));
const HelpTab = lazy(() => import("./dashboard/HelpTab"));
const MarketplaceTab = lazy(() => import("./dashboard/MarketplaceTab"));
const GroupsTab = lazy(() => import("./dashboard/GroupsTab"));
const ICalTab = lazy(() => import("./dashboard/ICalTab"));
const SwapsTab = lazy(() => import("./dashboard/SwapsTab"));
const BreakfastTab = lazy(() => import("./dashboard/BreakfastTab"));
const AdminTab = lazy(() => import("./dashboard/AdminTab"));
const FinancesTab = lazy(() => import("./dashboard/FinancesTab"));
const StudioTesoroTab = lazy(() => import("./dashboard/StudioTesoroTab"));
const TradeManagerTab = lazy(() => import("./dashboard/TradeManagerTab"));
const InvoicesTab = lazy(() => import("./dashboard/InvoicesTab"));
const WhatsAppTab = lazy(() => import("./dashboard/WhatsAppTab"));

// Preload lazy tabs on hover/focus.
const chunkPrefetchers = {
  ical: () => import("./dashboard/ICalTab"),
  marketplace: () => import("./dashboard/MarketplaceTab"),
  swaps: () => import("./dashboard/SwapsTab"),
  groups: () => import("./dashboard/GroupsTab"),
  breakfast: () => import("./dashboard/BreakfastTab"),
  analytics: () => import("./dashboard/AdvancedAnalyticsTab"),
  trade: () => import("./dashboard/TradeManagerTab"),
  channels: () => import("./dashboard/ChannelsTab"),
  hunts: () => import("./dashboard/HuntsTab"),
  help: () => import("./dashboard/HelpTab"),
  invoices: () => import("./dashboard/InvoicesTab"),
  admin: () => import("./dashboard/AdminTab"),
  studio: () => import("./dashboard/StudioTesoroTab"),
};
const prefetched = new Set();
const prefetchTab = (key) => {
  if (prefetched.has(key)) return;
  const fn = chunkPrefetchers[key];
  if (fn) { prefetched.add(key); fn().catch(() => prefetched.delete(key)); }
};

function TabLoading() {
  const { lang } = useLang();
  return (
    <div className="text-sm text-muted-foreground italic text-center py-16 border border-dashed border-border rounded-lg" data-testid="tab-loading">
      {lang === "it" ? "Caricamento…" : "Loading…"}
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const IT = lang === "it";
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const ALLOWED_TABS = ["property","rooms","bookings","calendar","reception","ical","swaps","groups","breakfast","analytics","invoices","finances","trade","marketplace","channels","admin","hunts","studio","help","whatsapp"];
  const LEGACY_TAB_ALIASES = { guests: "reception" };
  const requestedTab = searchParams.get("tab") || "";
  const initialTab = LEGACY_TAB_ALIASES[requestedTab] || (ALLOWED_TABS.includes(requestedTab) ? requestedTab : "calendar");
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const requested = searchParams.get("tab") || "";
    const resolved = LEGACY_TAB_ALIASES[requested] || (ALLOWED_TABS.includes(requested) ? requested : "calendar");
    if (resolved !== activeTab) setActiveTab(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    const current = searchParams.get("tab");
    if (activeTab !== "calendar" && current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", activeTab);
      setSearchParams(next, { replace: true });
    } else if (activeTab === "calendar" && current) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const refresh = async () => {
    try {
      const [p, r, b, o] = await Promise.all([
        api.get("/properties/me"), api.get("/rooms/me"),
        api.get("/bookings/me"), api.get("/marketplace/offers"),
      ]);
      setProperty(p.data); setRooms(r.data); setBookings(b.data); setOffers(o.data);
    } catch (e) { console.error("dashboard refresh failed", e); }
  };
  useEffect(() => { refresh(); }, []);

  const [todayCounts, setTodayCounts] = useState({ arrivals_pending: 0, departures_pending: 0, arrivals_total: 0, departures_total: 0 });
  useEffect(() => {
    let alive = true;
    const fetchCounts = () => api.get("/reception/today-counts")
      .then((r) => { if (alive) setTodayCounts(r.data || {}); })
      .catch(() => {});
    fetchCounts();
    const t = setInterval(fetchCounts, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!isAuthed()) return <Navigate to="/auth?mode=login" replace/>;
  const u0 = getUser();
  if (u0?.role === "trade") return <Navigate to="/trade" replace/>;
  if (!property) return <div className="min-h-screen grid place-items-center">{t("common.loading")}</div>;

  const user = getUser();
  const isAdmin = user?.is_superuser || user?.role === "admin" || user?.email === "admin@hospitalityhub.it";
  const revenue = bookings.filter(b => b.payment_status === "paid").reduce((s, b) => s + (b.amount || 0), 0);
  const openOffers = offers.filter(o => o.property_id === property.id).length;

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="dashboard"/>
      {user?.is_superuser && <SuperuserTierSwitcher/>}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2">{t("dash.welcome")}</div>
          <h1 className="font-display text-5xl tracking-tight" data-testid="dash-welcome">{user?.structure_name || property.name}</h1>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard testid="stat-bookings" label={t("dash.total_bookings")} value={bookings.length} icon={CalendarClock}/>
          <StatCard testid="stat-revenue" label={t("dash.revenue")} value={`€ ${revenue.toFixed(0)}`} icon={Star}/>
          <StatCard testid="stat-rooms" label={t("dash.rooms")} value={rooms.length} icon={BedDouble}/>
          <StatCard testid="stat-offers" label={t("dash.open_offers")} value={openOffers} icon={Store}/>
        </div>

        <div className={`grid gap-6 mt-6 ${["calendar", "bookings", "reception", "invoices"].includes(activeTab) ? "" : "lg:grid-cols-[minmax(0,1fr)_320px]"}`}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-w-0">
            <TabsList className="w-full flex-wrap h-auto justify-start mb-6" data-testid="dash-tabs">
              <TabsTrigger value="property" data-testid="tab-property"><Home className="w-4 h-4 mr-1"/>{t("dash.property")}</TabsTrigger>
              <TabsTrigger value="rooms" data-testid="tab-rooms"><BedDouble className="w-4 h-4 mr-1"/>{t("dash.rooms")}</TabsTrigger>
              <TabsTrigger value="bookings" data-testid="tab-bookings"><CalendarClock className="w-4 h-4 mr-1"/>{t("dash.bookings")}<TabBadge counts={todayCounts}/></TabsTrigger>
              <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarDays className="w-4 h-4 mr-1"/>{IT ? "Calendario" : "Calendar"}</TabsTrigger>
              <TabsTrigger value="reception" data-testid="tab-reception"><ConciergeBell className="w-4 h-4 mr-1"/>{IT ? "Reception" : "Reception"}<TabBadge counts={todayCounts}/></TabsTrigger>
              <TabsTrigger value="ical" data-testid="tab-ical" onMouseEnter={() => prefetchTab("ical")} onFocus={() => prefetchTab("ical")}><CalendarDays className="w-4 h-4 mr-1"/>{t("dash.ical")}</TabsTrigger>
              <TabsTrigger value="marketplace" data-testid="tab-marketplace" onMouseEnter={() => prefetchTab("marketplace")} onFocus={() => prefetchTab("marketplace")}><Store className="w-4 h-4 mr-1"/>{t("dash.marketplace")}</TabsTrigger>
              <TabsTrigger value="swaps" data-testid="tab-swaps" onMouseEnter={() => prefetchTab("swaps")} onFocus={() => prefetchTab("swaps")}><ArrowRightLeft className="w-4 h-4 mr-1"/>{t("dash.swaps")}</TabsTrigger>
              <TabsTrigger value="groups" data-testid="tab-groups" onMouseEnter={() => prefetchTab("groups")} onFocus={() => prefetchTab("groups")}><Users className="w-4 h-4 mr-1"/>{t("dash.groups")}</TabsTrigger>
              <TabsTrigger value="breakfast" data-testid="tab-breakfast" onMouseEnter={() => prefetchTab("breakfast")} onFocus={() => prefetchTab("breakfast")}><Coffee className="w-4 h-4 mr-1"/>{t("dash.breakfast")}</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics" onMouseEnter={() => prefetchTab("analytics")} onFocus={() => prefetchTab("analytics")}><BarChart3 className="w-4 h-4 mr-1"/>{t("dash.analytics")}</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-invoices" onMouseEnter={() => prefetchTab("invoices")} onFocus={() => prefetchTab("invoices")}><Receipt className="w-4 h-4 mr-1"/>{IT ? "Fatture" : "Invoices"}</TabsTrigger>
              <TabsTrigger value="finances" data-testid="tab-finances"><Wallet className="w-4 h-4 mr-1"/>{IT ? "Finanze" : "Finances"}</TabsTrigger>
              <TabsTrigger value="trade" data-testid="tab-trade" onMouseEnter={() => prefetchTab("trade")} onFocus={() => prefetchTab("trade")}><Briefcase className="w-4 h-4 mr-1"/>{t("dash.trade_tab")}</TabsTrigger>
              <TabsTrigger value="channels" data-testid="tab-channels" onMouseEnter={() => prefetchTab("channels")} onFocus={() => prefetchTab("channels")}><Plug className="w-4 h-4 mr-1"/>{t("dash.channels_tab")}</TabsTrigger>
              <TabsTrigger value="hunts" data-testid="tab-hunts" onMouseEnter={() => prefetchTab("hunts")} onFocus={() => prefetchTab("hunts")}><Compass className="w-4 h-4 mr-1"/>{IT ? "Esplora Mappa (Vista Ospite)" : "Explore Map (Guest View)"}</TabsTrigger>
              <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><MessageCircle className="w-4 h-4 mr-1"/>{IT ? "Msg Auto" : "Auto Msg"}</TabsTrigger>
              <TabsTrigger value="help" data-testid="tab-help" onMouseEnter={() => prefetchTab("help")} onFocus={() => prefetchTab("help")}><HelpCircle className="w-4 h-4 mr-1"/>Help</TabsTrigger>
              {isAdmin && <TabsTrigger value="admin" data-testid="tab-admin" className="!bg-sage/10 !text-sage !border !border-sage/30 hover:!bg-sage hover:!text-white data-[state=active]:!bg-sage data-[state=active]:!text-white transition-colors" onMouseEnter={() => prefetchTab("admin")} onFocus={() => prefetchTab("admin")}><ShieldCheck className="w-4 h-4 mr-1"/>{t("dash.admin")}</TabsTrigger>}
              {user?.is_superuser && <TabsTrigger value="studio" data-testid="tab-studio" className="!bg-sage/10 !text-sage !border !border-sage/30 hover:!bg-sage hover:!text-white data-[state=active]:!bg-sage data-[state=active]:!text-white transition-colors" onMouseEnter={() => prefetchTab("studio")} onFocus={() => prefetchTab("studio")}><Sparkles className="w-4 h-4 mr-1"/>{IT ? "Studio del Tesoro (Gestione Contenuti)" : "Treasure Studio (Content Management)"}</TabsTrigger>}
            </TabsList>

            <TabsContent value="property"><PropertyTab property={property} refresh={refresh}/></TabsContent>
            <TabsContent value="rooms"><RoomsTab propertyId={property.id} refreshTop={refresh}/></TabsContent>
            <TabsContent value="bookings"><BookingsTab property={property} rooms={rooms}/></TabsContent>
            <TabsContent value="finances"><Suspense fallback={<TabLoading/>}><FinancesTab/></Suspense></TabsContent>
            <TabsContent value="calendar"><CalendarTab rooms={rooms}/></TabsContent>
            <TabsContent value="reception"><ReceptionTab/></TabsContent>
            <TabsContent value="ical"><Suspense fallback={<TabLoading/>}><ICalTab rooms={rooms}/></Suspense></TabsContent>
            <TabsContent value="marketplace"><Suspense fallback={<TabLoading/>}><MarketplaceTab property={property} rooms={rooms}/></Suspense></TabsContent>
            <TabsContent value="swaps"><Suspense fallback={<TabLoading/>}><SwapsTab property={property} bookings={bookings}/></Suspense></TabsContent>
            <TabsContent value="groups"><Suspense fallback={<TabLoading/>}><GroupsTab/></Suspense></TabsContent>
            <TabsContent value="breakfast"><Suspense fallback={<TabLoading/>}><BreakfastTab property={property}/></Suspense></TabsContent>
            <TabsContent value="analytics"><Suspense fallback={<TabLoading/>}><AdvancedAnalyticsTab/></Suspense></TabsContent>
            <TabsContent value="trade"><Suspense fallback={<TabLoading/>}><TradeManagerTab/></Suspense></TabsContent>
            <TabsContent value="channels"><Suspense fallback={<TabLoading/>}><ChannelsTab/></Suspense></TabsContent>
            <TabsContent value="hunts"><Suspense fallback={<TabLoading/>}><HuntsTab/></Suspense></TabsContent>
            <TabsContent value="whatsapp"><Suspense fallback={<TabLoading/>}><WhatsAppTab/></Suspense></TabsContent>
            <TabsContent value="help"><Suspense fallback={<TabLoading/>}><HelpTab AssistantIACard={AssistantIACard}/></Suspense></TabsContent>
            <TabsContent value="invoices"><Suspense fallback={<TabLoading/>}><InvoicesTab property={property} refresh={refresh}/></Suspense></TabsContent>
            {isAdmin && <TabsContent value="admin"><Suspense fallback={<TabLoading/>}><AdminTab/></Suspense></TabsContent>}
            {user?.is_superuser && <TabsContent value="studio"><Suspense fallback={<TabLoading/>}><StudioTesoroTab/></Suspense></TabsContent>}
          </Tabs>

          {!["calendar", "bookings", "reception", "invoices"].includes(activeTab) && (
          <aside className="space-y-6 lg:sticky lg:top-6 lg:self-start" data-testid="dash-sidebar">
            <SubscriptionCard/>
            <div className="border border-border rounded-lg p-5 bg-card" data-testid="dash-pricing-preview">
              <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-1">
                {IT ? "Piani e prezzi" : "Plans & pricing"}
              </div>
              <h2 className="font-display text-xl mb-3">
                {IT ? "Confronta e migliora" : "Compare & upgrade"}
              </h2>
              <PricingPreview compact={true} showLink={true}/>
            </div>
            <BoostCard compact={true}/>
          </aside>
          )}
        </div>
      </div>
    </div>
  );
}
